import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    AUTH_ARTWORK,
    AUTH_ARTWORK_INTERVAL_MS,
    AUTH_ARTWORK_MAX_RENDER_WIDTH,
    getAuthArtworkSrc,
    getAuthArtworkSrcSet,
} from '../../config/authArtwork';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import styles from './AuthShell.module.css';

const SIZES = `(max-width: 767px) 60vw, ${AUTH_ARTWORK_MAX_RENDER_WIDTH}px`;
const CROSSFADE_MS = 1100;

function preloadArtwork(artwork) {
    return new Promise((resolve, reject) => {
        if (typeof Image === 'undefined') { resolve(); return; }
        const img = new Image();
        img.onload = () => resolve(artwork);
        img.onerror = () => reject(artwork);
        img.sizes = SIZES;
        img.srcset = getAuthArtworkSrcSet(artwork) || '';
        img.src = getAuthArtworkSrc(artwork) || '';
    });
}

/**
 * AuthArtworkPanel
 *
 * Cinematic panel that shows one piece of Aluvo-owned collectible artwork at a
 * time, optionally rotating through a small curated set with a slow crossfade.
 *
 * - Only the current (and, mid-fade, the previous) image is mounted.
 * - The next artwork is preloaded ahead of its turn so it never flashes.
 * - Artwork that fails to load is dropped from the pool; if nothing loads the
 *   panel degrades to the atmospheric gradient + brand copy.
 * - Rotation is disabled for `prefers-reduced-motion` and while the tab is hidden.
 */
export default function AuthArtworkPanel({
    artworks = AUTH_ARTWORK,
    initialIndex = 0,
    rotate = true,
    intervalMs = AUTH_ARTWORK_INTERVAL_MS,
    onArtworkChange,
    showSupportingLine = true,
}) {
    const reducedMotion = useReducedMotion();
    const [failedIds, setFailedIds] = useState(() => new Set());
    const available = useMemo(() => artworks.filter((a) => !failedIds.has(a.id)), [artworks, failedIds]);

    const [currentId, setCurrentId] = useState(() => artworks[initialIndex % Math.max(artworks.length, 1)]?.id || null);
    const [previous, setPrevious] = useState(null);
    const fadeTimer = useRef(null);
    const preloadedIds = useRef(new Set());

    const current = useMemo(
        () => available.find((a) => a.id === currentId) || available[0] || null,
        [available, currentId],
    );
    const currentRef = useRef(current);
    currentRef.current = current;

    const shouldRotate = rotate && !reducedMotion && available.length > 1;

    const goTo = useCallback((nextArtwork) => {
        const prevArtwork = currentRef.current;
        if (!nextArtwork || prevArtwork?.id === nextArtwork.id) return;
        if (prevArtwork && !reducedMotion) {
            setPrevious(prevArtwork);
            if (fadeTimer.current) clearTimeout(fadeTimer.current);
            fadeTimer.current = setTimeout(() => setPrevious(null), CROSSFADE_MS);
        }
        setCurrentId(nextArtwork.id);
    }, [reducedMotion]);

    const markFailed = useCallback((artwork) => {
        if (!artwork) return;
        setFailedIds((prev) => {
            if (prev.has(artwork.id)) return prev;
            const next = new Set(prev);
            next.add(artwork.id);
            return next;
        });
    }, []);

    // Keep the parent informed (used for analytics `artwork_id`).
    useEffect(() => {
        if (onArtworkChange) onArtworkChange(current?.id || null);
    }, [current?.id, onArtworkChange]);

    // Preload the next piece as soon as the current one is shown.
    useEffect(() => {
        if (!shouldRotate || !current) return undefined;
        const idx = available.findIndex((a) => a.id === current.id);
        const next = available[(idx + 1) % available.length];
        if (!next || preloadedIds.current.has(next.id)) return undefined;
        let cancelled = false;
        preloadArtwork(next)
            .then(() => { if (!cancelled) preloadedIds.current.add(next.id); })
            .catch(() => { if (!cancelled) markFailed(next); });
        return () => { cancelled = true; };
    }, [shouldRotate, current, available, markFailed]);

    // Slow rotation, paused when the document is hidden.
    useEffect(() => {
        if (!shouldRotate || !current) return undefined;
        let timer = null;
        const tick = () => {
            const idx = available.findIndex((a) => a.id === current.id);
            goTo(available[(idx + 1) % available.length]);
        };
        const start = () => {
            if (timer) return;
            timer = setInterval(tick, intervalMs);
        };
        const stop = () => {
            if (timer) clearInterval(timer);
            timer = null;
        };
        const onVisibility = () => (document.hidden ? stop() : start());
        if (typeof document !== 'undefined' && !document.hidden) start();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [shouldRotate, current, available, goTo, intervalMs]);

    useEffect(() => () => { if (fadeTimer.current) clearTimeout(fadeTimer.current); }, []);

    const accent = current?.accent || 'var(--color-gold)';
    const tierLabel = current?.tier === 'legendary' ? 'Legendary' : 'Collection';

    return (
        <aside
            className={styles.artPanel}
            style={{ '--auth-accent': accent }}
            data-artwork-id={current?.id || 'none'}
            data-rotating={shouldRotate ? 'true' : 'false'}
            data-reduced-motion={reducedMotion ? 'true' : 'false'}
            aria-label="Aluvo collection artwork"
        >
            <div className={styles.artBackdrop} aria-hidden="true" />

            <div className={styles.artStage}>
                {previous && previous.id !== current?.id && (
                    <img
                        key={`prev-${previous.id}`}
                        className={`${styles.artImage} ${styles.artImageLeaving}`}
                        src={getAuthArtworkSrc(previous)}
                        srcSet={getAuthArtworkSrcSet(previous)}
                        sizes={SIZES}
                        alt=""
                        aria-hidden="true"
                        decoding="async"
                    />
                )}
                {current && (
                    <img
                        key={`cur-${current.id}`}
                        className={`${styles.artImage} ${reducedMotion ? '' : styles.artImageEntering}`}
                        src={getAuthArtworkSrc(current)}
                        srcSet={getAuthArtworkSrcSet(current)}
                        sizes={SIZES}
                        alt={current.altText}
                        decoding="async"
                        fetchPriority="high"
                        onError={() => markFailed(current)}
                        style={{ objectPosition: `${(current.focalPoint?.x ?? 0.5) * 100}% ${(current.focalPoint?.y ?? 0.5) * 100}%` }}
                    />
                )}
                {!current && <div className={styles.artFallback} aria-hidden="true" data-testid="auth-artwork-fallback" />}
            </div>

            <div className={styles.artOverlay} aria-hidden="true" />

            <div className={styles.artContent}>
                <Link to="/" className={styles.artBrand} aria-label="Aluvo home">
                    <img src="/logo192.png" alt="" className={styles.artLogo} width="40" height="40" />
                    <span className={styles.artWordmark}>Aluvo</span>
                </Link>
                <p className={styles.artHeadline}>
                    Verify what you watched.
                    <br />
                    Build your collection.
                </p>
                {showSupportingLine && (
                    <p className={styles.artSupport}>Earn coins. Upgrade your collection. Prove your fandom.</p>
                )}
                {current && (
                    <p className={styles.artCaption}>
                        <span className={`${styles.tierPill} ${current.tier === 'legendary' ? styles.tierPillLegendary : ''}`}>
                            {tierLabel}
                        </span>
                        <span className={styles.artCaptionText}>
                            {current.title} · {current.collection}
                        </span>
                    </p>
                )}
            </div>
        </aside>
    );
}
